// @flow
import classnames from 'classnames';

import SettingDetail from '@components/dyor/SettingDetail';
import FormattedPrice from '@components/FormattedPrice';

import * as routes from '@src/app_routes/settings';
import css from '@styles/dyor';

import type { DyorSettingType } from '@types/setting';

type ImagePropsType = {
  setting: DyorSettingType
};

type SpecsPropsType = {
  setting: DyorSettingType
};

type EmptyPropsType = {
  active: boolean,
  setVisibleDetails: (string | null) => void
};

type PropsType = {
  setting: ?DyorSettingType,
  active: boolean,
  detailsAreVisible: boolean,
  setVisibleDetails: (string | null) => void,
  containerRef: { current: null | React.ElementRef<'div'> },
  chainPosition: number
};

const placeholderImage = '/images/ring-builder/setting.png';

function EmptySetting ({ active, setVisibleDetails }: EmptyPropsType) {
  const onClick = React.useCallback(
    () => {
      setVisibleDetails(null);
      if (!window) return;
      if (window.location.pathname !== routes.searchPath()) {
        window.location = routes.searchPath();
      }
    },
    [active, setVisibleDetails]
  );

  return (
    <section
      className={classnames(css.section, 'empty', {
        active
      })}
      onClick={onClick}>
      <PlaceholderImage />
      <div className={css.specifications}>
        <h3 className={css.sectionHeader}>
          <span className={css.chooseText}>Choose </span>
          <span className={css.chooseAText}>a </span>Setting
        </h3>
        {active && <p className={css.sectionCaption}>Select your setting</p>}
      </div>
      <div
        className={classnames({
          [css['blueChevron']]: active,
          [css['greyChevron']]: !active
        })}
      />
    </section>
  );
}

function SettingImage ({ setting }: ImagePropsType) {
  return (
    <div className={css.imageContainer}>
      <img src={setting.incomplete_image.thumbnail} alt="Setting image" />
    </div>
  );
}

function PlaceholderImage () {
  return (
    <div className={css.imageContainer}>
      <img src={placeholderImage} alt="Placeholder setting image" />
    </div>
  );
}

function SettingSpecifications ({ setting }: SpecsPropsType) {
  const shortCaption = setting.caption_description
    .split(' ')
    .slice(0, 2) // Only keep the first two words
    .join(' ');
  return (
    <div className={css.specifications}>
      <h3 className={css.sectionHeader}>
        <span className={css.sectionPronoun}>Your </span>Setting
      </h3>
      <p className={css.sectionCaption}>
        {shortCaption}
        <span className={css.sectionPrice}>
          {' '}
          - <FormattedPrice value={setting.retail_price} />
        </span>
      </p>
    </div>
  );
}

export default function Setting ({
  setting,
  active,
  detailsAreVisible,
  setVisibleDetails,
  containerRef,
  chainPosition
}: PropsType) {
  const detailsRef = React.useRef(null);
  const tileRef = React.useRef(null);

  const toggleDetails = React.useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!(e.target instanceof Node)) return;
      if (detailsRef.current && detailsRef.current.contains(e.target)) return;
      if (!containerRef.current) return;
      if (!tileRef.current) return;
      if (
        containerRef.current.contains(e.target) &&
        !tileRef.current.contains(e.target)
      )
        return;

      e.stopPropagation();

      setVisibleDetails(detailsAreVisible ? null : 'setting');
    },
    [containerRef, detailsAreVisible, setVisibleDetails]
  );

  React.useEffect(
    () => {
      if (document) {
        if (detailsAreVisible) {
          if (window.PointerEvent) {
            document.addEventListener('pointerup', toggleDetails);
          } else {
            document.addEventListener('click', toggleDetails);
            document.addEventListener('touchend', toggleDetails);
          }
        } else {
          if (window.PointerEvent) {
            document.removeEventListener('pointerup', toggleDetails);
          } else {
            document.removeEventListener('click', toggleDetails);
            document.removeEventListener('touchend', toggleDetails);
          }
        }
      }

      return () => {
        if (document) {
          if (window.PointerEvent) {
            document.removeEventListener('pointerup', toggleDetails);
          } else {
            document.removeEventListener('click', toggleDetails);
            document.removeEventListener('touchend', toggleDetails);
          }
        }
      };
    },
    [detailsAreVisible, toggleDetails]
  );

  if (!setting) {
    return (
      <EmptySetting active={active} setVisibleDetails={setVisibleDetails} />
    );
  }

  return (
    <div
      className={classnames(css.section, { active })}
      onPointerUp={toggleDetails}
      onClick={() => {
        // We still support onclick for old browsers that don't have PointerEvent. We don't want to call toggleDetails
        // for both events though
        if (window.PointerEvent) return;

        return toggleDetails();
      }}
      ref={tileRef}>
      <SettingSpecifications setting={setting} />
      <SettingImage setting={setting} />
      <SettingDetail
        setting={setting}
        show={detailsAreVisible}
        ref={detailsRef}
        chainPosition={chainPosition}
        containerRef={containerRef}
      />
      <div
        className={classnames({
          [css['blueChevron']]: active,
          [css['greyChevron']]: !active
        })}
      />
    </div>
  );
}
